import { Injectable, inject } from '@angular/core';
import { Observable, map, of, switchMap } from 'rxjs';

import { PcChatApiService } from '../api/pc-chat-api.service';
import { extractAssistantText, extractConversationId } from '../api/pc-chat-response-normalizer';
import {
  ConversationStatus as ApiConversationStatus,
  ConversationResponse,
  FeedbackResponse,
  MessageResponse,
  StepResponse,
} from '../api/pc-chat-api.types';
import {
  ChatBackend,
  ConversationListPage,
  ConversationMessagesResponse,
  CreateResponseArgs,
  FeedbackSubmitResult,
} from './chat-backend';
import { formatUpdatedAtLabel } from './chat-date';
import {
  AssistantStep,
  ChatMessage,
  Conversation,
  ConversationStatus,
  FeedbackRating,
} from './chat.models';
import { ConversationSearchResponse } from '../search/search.models';

function normalizeText(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value).trim();
  return '';
}

function normalizeConversationStatus(
  raw: ApiConversationStatus | string | null | undefined,
): ConversationStatus {
  const s = (raw ?? '').toString().trim().toLowerCase();
  if (s === 'running' || s === 'completed' || s === 'failed' || s === 'cancelled') return s;
  return null;
}

function normalizeFeedbackRating(raw: unknown): FeedbackRating {
  const s = (raw ?? '').toString().trim().toLowerCase();
  if (s === 'positive' || s === 'negative') return s;
  return null;
}

function coerceStepArray(value: unknown): StepResponse[] {
  if (Array.isArray(value)) return value as StepResponse[];

  // Be tolerant: some backends serialize JSON columns as strings.
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as unknown;
      if (Array.isArray(parsed)) return parsed as StepResponse[];
    } catch {
      // ignore
    }
  }

  // Some backends nest: { steps: [...] }
  if (value && typeof value === 'object' && 'steps' in (value as Record<string, unknown>)) {
    const nested = (value as Record<string, unknown>)['steps'];
    if (Array.isArray(nested)) return nested as StepResponse[];
  }

  return [];
}

function coerceMetadataRecord(value: unknown): Record<string, unknown> | null {
  if (value === null || value === undefined) return null;

  // Be tolerant: some backends serialize JSON columns as strings.
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed))
        return parsed as Record<string, unknown>;
    } catch {
      // ignore
    }
    return null;
  }

  if (value && typeof value === 'object' && !Array.isArray(value))
    return value as Record<string, unknown>;
  return null;
}

function extractConversationStatus(c: ConversationResponse): ConversationStatus | undefined {
  const rec = c as unknown as Record<string, unknown>;

  // Prefer a top-level status if the backend provides it.
  const topLevelStatus = normalizeConversationStatus(
    rec['status'] as ApiConversationStatus | string | null | undefined,
  );
  if (topLevelStatus) return topLevelStatus;

  // Backends can return metadata as `metadata` or `metadata_`.
  const meta = coerceMetadataRecord(rec['metadata'] ?? rec['metadata_']);
  const metaStatus = normalizeConversationStatus(
    meta?.['status'] as ApiConversationStatus | string | null | undefined,
  );
  if (metaStatus) return metaStatus;

  // Unknown: return undefined (preserves any locally-known status during refresh).
  return undefined;
}

function toConversation(c: ConversationResponse): Conversation {
  const updatedAt = c.updated_at ?? c.created_at;
  const status = extractConversationStatus(c);

  const base: Conversation = {
    id: String(c.id),
    title: c.title,
    updatedAtLabel: formatUpdatedAtLabel(updatedAt),
    feedbackRating: normalizeFeedbackRating(c.feedback_rating),
  };

  return status ? { ...base, status } : base;
}

/**
 * Returns `null` when the step should be completely removed from UI (error status or unidentifiable).
 * Returns `{ remove: true, key }` when we need to remove an existing step with this key.
 */
type MapStepResult = AssistantStep | { remove: true; key: string } | null;

function mapStepToUi(step: StepResponse, ctx: { messageId: string; index: number }): MapStepResult {
  const rec = step as unknown as Record<string, unknown>;

  // Backends aren't always consistent with key casing across environments.
  const stepId = normalizeText(rec['step_id'] ?? rec['stepId'] ?? rec['id']);
  const name = normalizeText(rec['name'] ?? rec['title'] ?? rec['tool_name'] ?? rec['toolName']);

  // If we can't identify the step at all, don't render it.
  if (!stepId && !name) return null;

  const statusRaw = normalizeText(rec['status']).toLowerCase();
  const description = normalizeText(rec['description']);
  const safeName = name || `Step ${ctx.index + 1}`;
  const safeStepId = stepId || ctx.messageId;
  const key = `${safeStepId}:${safeName}`;

  // Steps with error status should be removed entirely from the UI.
  if (statusRaw === 'error') {
    return { remove: true, key };
  }

  const status: AssistantStep['status'] = statusRaw === 'completed' ? 'completed' : 'in_progress';

  return {
    // Stable across reloads: ties the step to the persisted backend message id.
    key,
    stepId: safeStepId,
    name: safeName,
    description: description ? description : undefined,
    status,
  };
}

function mergeStepsPreserveOrder(steps: AssistantStep[]): AssistantStep[] {
  const byKey = new Map<string, AssistantStep>();
  const order: string[] = [];

  for (const s of steps) {
    if (!byKey.has(s.key)) order.push(s.key);
    byKey.set(s.key, s);
  }

  return order.map((k) => byKey.get(k)!).filter(Boolean);
}

function parseCreatedAtMs(value: string | null | undefined): number {
  const raw = (value ?? '').trim();
  if (!raw) return 0;

  // Backend may return microseconds without timezone, e.g. `2025-12-26T10:32:50.810863`.
  // JS Date.parse() can fail on microseconds, so truncate to milliseconds and pin to UTC.
  const truncated = raw.replace(/(\.\d{3})\d+/, '$1');
  const hasTz = /[zZ]$|[+-]\d{2}:\d{2}$/.test(truncated);
  const normalized = hasTz ? truncated : `${truncated}Z`;

  const parsed = Date.parse(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeMessagesForUi(messagesChronological: MessageResponse[]): ChatMessage[] {
  const out: ChatMessage[] = [];
  let pendingSteps: AssistantStep[] = [];
  let lastBackendMessageId: string | null = null;
  let sawRunningAssistant = false;

  for (const m of messagesChronological) {
    const id = String(m.id);
    const role = (m.role ?? '').toLowerCase();
    lastBackendMessageId = id;

    if (role === 'user') {
      // Reset any dangling steps; steps logically belong to the assistant response chain.
      pendingSteps = [];
      out.push({ id, role: 'user', content: m.content ?? '' });
      continue;
    }

    const msgStatus = normalizeConversationStatus(
      (m as unknown as { status?: unknown }).status as string | null | undefined,
    );
    if (msgStatus === 'running') sawRunningAssistant = true;

    // Assistant messages can include tool-call frames where `content` is null; these carry steps.
    const rawSteps = coerceStepArray(m.steps);
    if (rawSteps.length) {
      const mapped = rawSteps.map((s, idx) => mapStepToUi(s, { messageId: id, index: idx }));

      // Collect keys to remove (steps with error status).
      const keysToRemove = new Set(
        mapped
          .filter((r): r is { remove: true; key: string } => !!r && 'remove' in r)
          .map((r) => r.key),
      );

      // Remove error steps from pending list.
      if (keysToRemove.size) {
        pendingSteps = pendingSteps.filter((s) => !keysToRemove.has(s.key));
      }

      // Add/merge non-error steps.
      const validSteps = mapped.filter((r): r is AssistantStep => !!r && !('remove' in r));
      if (validSteps.length)
        pendingSteps = mergeStepsPreserveOrder([...pendingSteps, ...validSteps]);
    }

    const hasContent = typeof m.content === 'string' && m.content.trim().length > 0;
    // Skip empty assistant bubbles, except for failed ones (to show the Retry button)
    if (!hasContent && msgStatus !== 'failed' && msgStatus !== 'cancelled') {
      // Skip rendering empty assistant bubbles; their steps are merged into the next answer.
      continue;
    }

    const assistant: ChatMessage = {
      id,
      role: 'assistant',
      label: 'Analysis',
      contentMarkdown: m.content ?? '',
      status:
        msgStatus === 'running'
          ? 'in_progress'
          : msgStatus === 'failed'
            ? 'error'
            : msgStatus === 'cancelled'
              ? 'cancelled'
              : 'completed',
    };

    if (pendingSteps.length) assistant.steps = pendingSteps;
    if (typeof m.token_count === 'number' && Number.isFinite(m.token_count) && m.token_count > 0) {
      assistant.tokenUsage = { totalTokens: m.token_count };
    }
    pendingSteps = [];
    out.push(assistant);
  }

  // If the client reloads / loses connection mid-run, the backend may have only persisted tool/step frames
  // (assistant messages with null/empty `content`) so far. Our loop above intentionally skips rendering
  // those empty assistant bubbles and merges their steps into the *next* assistant answer.
  //
  // When there is no next answer yet, we must flush the accumulated steps into a visible placeholder,
  // otherwise the user sees nothing until the final assistant message is written to the DB.
  if (pendingSteps.length || sawRunningAssistant) {
    const placeholderId = `pending_steps_${lastBackendMessageId ?? 'unknown'}`;
    out.push({
      id: placeholderId,
      role: 'assistant',
      label: 'Analysis',
      contentMarkdown: '',
      status: 'in_progress',
      steps: pendingSteps.length ? pendingSteps : undefined,
    });
  }

  return out;
}

@Injectable({ providedIn: 'root' })
export class ApiChatBackendService implements ChatBackend {
  private readonly api = inject(PcChatApiService);

  listConversations(page = 1, limit = 20): Observable<ConversationListPage> {
    return this.api.listConversations(page, limit).pipe(
      map((res) => ({
        conversations: res.conversations.map(toConversation),
        pagination: res.pagination,
      })),
    );
  }

  searchConversations(query: string, limit = 20): Observable<ConversationSearchResponse> {
    const q = (query ?? '').trim();
    if (!q) return of({ results: [], query: '', totalCount: 0 });

    return this.api.searchConversations(q, limit).pipe(
      map((res) => ({
        query: (res.query ?? '').trim(),
        totalCount: typeof res.total_count === 'number' ? res.total_count : 0,
        results: (res.results ?? []).map((r) => ({
          conversationId: String(r.conversation_id),
          title: r.title ?? '',
          snippet: r.snippet ?? '',
          matchType: r.match_type ?? '',
          createdAtLabel: formatUpdatedAtLabel(r.created_at),
        })),
      })),
    );
  }

  listMessages(
    conversationId: string,
    page = 1,
    limit = 100,
  ): Observable<ConversationMessagesResponse> {
    const id = Number(conversationId);
    if (!Number.isFinite(id))
      return of({
        messages: [],
        status: null,
        deepResearch: false,
        pagination: { page: 1, limit: 100, total_pages: 1, total_count: 0 },
      });

    return this.api.listMessages(id, page, limit).pipe(
      map((res) => {
        const listStatus = normalizeConversationStatus(res.status);
        const chronological = [...res.messages].sort((a, b) => {
          const ta = parseCreatedAtMs(a.created_at);
          const tb = parseCreatedAtMs(b.created_at);

          // If either timestamp can't be parsed, fall back to DB insertion order (id).
          // Otherwise steps can get sorted before the user message and then be reset/dropped.
          if (ta === 0 || tb === 0) return a.id - b.id;

          const byTime = ta - tb;
          return byTime !== 0 ? byTime : a.id - b.id;
        });
        const normalized = normalizeMessagesForUi(chronological);

        // Reload mid-run: backend can persist only the user message initially.
        // If the messages endpoint says we're still `running`, we must show an in-progress assistant
        // placeholder so the UI can keep polling until an answer is persisted.
        const shouldShowRunningPlaceholder = listStatus === 'running';
        const hasInProgressAssistant = normalized.some(
          (m) => m.role === 'assistant' && (m.status ?? 'completed') === 'in_progress',
        );

        // If the conversation failed but there's no error assistant message, add a placeholder
        // so the Retry button can appear.
        const shouldShowFailedPlaceholder = listStatus === 'failed';
        const hasErrorAssistant = normalized.some(
          (m) => m.role === 'assistant' && m.status === 'error',
        );

        const shouldShowCancelledPlaceholder = listStatus === 'cancelled';
        const hasCancelledAssistant = normalized.some(
          (m) => m.role === 'assistant' && m.status === 'cancelled',
        );

        let messages = normalized;
        if (shouldShowRunningPlaceholder && !hasInProgressAssistant) {
          messages = [
            ...normalized,
            {
              id: `pending_poll_${conversationId}`,
              role: 'assistant',
              label: 'Analysis',
              contentMarkdown: '',
              status: 'in_progress',
            } as const,
          ];
        } else if (shouldShowCancelledPlaceholder && !hasCancelledAssistant) {
          const withoutInProgress = normalized.filter(
            (m) => !(m.role === 'assistant' && m.status === 'in_progress'),
          );

          messages = [
            ...withoutInProgress,
            {
              id: `cancelled_${conversationId}`,
              role: 'assistant',
              label: 'Analysis',
              contentMarkdown: 'You stopped this response',
              status: 'cancelled',
            } as const,
          ];
        } else if (shouldShowFailedPlaceholder && !hasErrorAssistant) {
          // Collect steps from in_progress placeholders before removing them.
          // These are artifacts from the pending-steps flush that should not
          // render as separate streaming cards when the conversation has failed.
          const inProgressSteps = normalized
            .filter(
              (m): m is Extract<ChatMessage, { role: 'assistant' }> =>
                m.role === 'assistant' && m.status === 'in_progress',
            )
            .flatMap((m) => m.steps ?? []);

          const withoutInProgress = normalized.filter(
            (m) => !(m.role === 'assistant' && m.status === 'in_progress'),
          );

          messages = [
            ...withoutInProgress,
            {
              id: `failed_${conversationId}`,
              role: 'assistant',
              label: 'Analysis',
              contentMarkdown: 'Request failed.',
              status: 'error',
              ...(inProgressSteps.length ? { steps: inProgressSteps } : {}),
            } as const,
          ];
        }

        return {
          messages,
          status: listStatus,
          deepResearch: !!res.deep_research,
          pagination: res.pagination,
        };
      }),
    );
  }

  renameConversation(conversationId: string, title: string): Observable<void> {
    const id = Number(conversationId);
    if (!Number.isFinite(id)) return of(void 0);
    return this.api.updateConversation(id, { title }).pipe(map(() => void 0));
  }

  deleteConversation(conversationId: string): Observable<void> {
    const id = Number(conversationId);
    if (!Number.isFinite(id)) return of(void 0);
    return this.api.deleteConversation(id).pipe(map(() => void 0));
  }

  submitFeedback(
    conversationId: string,
    rating: string,
    comment = '',
  ): Observable<FeedbackSubmitResult> {
    const id = Number(conversationId);
    if (!Number.isFinite(id)) {
      return of({
        feedbackRating: normalizeFeedbackRating(rating),
        feedbackComment: (comment ?? '').trim() ? comment : null,
        feedbackAt: new Date().toISOString(),
      });
    }
    return this.api
      .submitFeedback(id, { rating, comment })
      .pipe(map((res: FeedbackResponse) => this.toFeedbackResult(res)));
  }

  private toFeedbackResult(res: FeedbackResponse): FeedbackSubmitResult {
    return {
      feedbackRating: normalizeFeedbackRating(res.feedback_rating),
      feedbackComment: res.feedback_comment ?? null,
      feedbackAt: res.feedback_at,
    };
  }

  stopConversation(conversationId: string): Observable<void> {
    const id = Number(conversationId);
    if (!Number.isFinite(id)) return of(void 0);
    return this.api.stopConversation(id).pipe(map(() => void 0));
  }

  createResponse(args: CreateResponseArgs): Observable<unknown> {
    const conversation_id =
      args.conversationId && Number.isFinite(Number(args.conversationId))
        ? Number(args.conversationId)
        : null;

    const modelRaw = (args.model ?? '').trim();
    const model = modelRaw ? modelRaw : 'fast';

    const request = {
      input: args.input,
      conversation_id,
      model,
      deep_research: args.deepResearch ?? false,
      stream: args.stream,
      ...(args.retry ? { retry: true } : {}),
      ...(args.edited ? { edited: true } : {}),
    };

    if (args.stream) return this.api.createResponseStream(request);

    // Non-streaming: emit the JSON payload once.
    return this.api.createResponse(request).pipe(
      // Ensure the observable emits at least something for caller code paths that expect it.
      switchMap((payload) => {
        const maybeId = extractConversationId(payload);
        const maybeText = extractAssistantText(payload);
        return of({ payload, conversation_id: maybeId, text: maybeText });
      }),
    );
  }
}
